import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';
import {
  accountPayableListGrouping,
  accountPayableRangeDate,
  cancelInstallmentPayable,
  createInstallment,
  generateGrouping,
  getListInstallments,
  listInstallments,
  reverseInstallmentPayable,
  renegotiateAccountPayable,
  settledMultipleInstallmentPayable,
  settledPartiallyInstallmentPayable,
  settledSingleInstallmentPayable,
  splitInstallmentPayable,
  updateAccountPayableAndInstallmentPayable,
  generateFinancialByPurchase,
  accountPayableListByPurchase,
  updateAccountPayableReverseMultipleInstallment,
  updateAccountPayableDeleteMultipleInstallment,
  accountPayableListGroupingById,
  accountPayableArchiveCreate,
  accountPayableArchiveList,
  accountPayableArchiveRemove,
  accountPayableIncludeTag,
} from './AccountPayable.service';
import {
  AccountPayableGroupingRequest,
  AccountPayableRangeDateRequest,
  AccountPayableReleaseRequest,
  AccountPayableReleaseUpdateRequest,
  InstallmentCancelRequest,
  InstallmentReverseRequest,
  InstallmentRenegotiateRequest,
  InstallmentSettlePartiallyRequest,
  InstallmentSettleSingleRequest,
  InstallmentSplitRequest,
  ListInstallmentsQuery,
  GenerateFinancialByPurchase,
  AccountPayableListByPurchase,
} from './AccountPayable.types';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import {
  AccountPayableErrors,
  handleInstallmentAlreadyEdited,
} from './AccountPayable.errors';
import { AxiosError } from 'axios';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { FINANCIAL_CONSULT_PAYABLE_LIST } from '../Consultation/consultation.hooks';

export function useInstallmentsList(
  params?: ListInstallmentsQuery,
  enabled?: boolean,
) {
  const query = useInfiniteQuery(
    ['installment-list', { ...params }],
    ({ pageParam }) => {
      const req: ListInstallmentsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listInstallments(req);
    },
    {
      enabled: enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useInstallmentCreate() {
  const queryChanel = useQueryChanel();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: AccountPayableReleaseRequest) => createInstallment(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.create.success.content',
          }),
        });

        queryChanel({
          invalidateQueries: [[FINANCIAL_CONSULT_PAYABLE_LIST]],
          resetQueries: [['dashboard-data'], ['installment-list']],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useAccountPayableReturnListInstallments(
  accountPayableId: number,
  enabled = true,
) {
  const query = useQuery(
    ['installmentList-detail', accountPayableId],
    () => getListInstallments(accountPayableId),
    {
      enabled: !!accountPayableId && enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useAccountPayableAndInstallmentUpdate() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: AccountPayableReleaseUpdateRequest) =>
      updateAccountPayableAndInstallmentPayable(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.update.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.update.success.content',
          }),
        });

        queryChanel({
          invalidateQueries: [
            [
              'installment-detail',
              data.accountPayableId,
              data.installmentPayableId,
            ],
            ['installmentList-detail', data.accountPayableId],
            ['installment-list', { bankAccountId: data.bankAccountId }],
          ],
          resetQueries: [['dashboard-data']],
        });
      },

      async onError(error: AxiosError, data) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountPayableErrors[err] || err),
        });

        if (handleInstallmentAlreadyEdited(error)) {
          await queryClient.invalidateQueries([
            'installment-detail',
            data.accountPayableId,
            data.installmentPayableId,
          ]),
            await queryClient.invalidateQueries([
              'installmentList-detail',
              data.accountPayableId,
            ]);
        }
      },
    },
  );

  return mutation;
}

export function useInstallmentCancel() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentCancelRequest) => cancelInstallmentPayable(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.notifications.remove.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.notifications.remove.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [['installment-list']],
          resetQueries: [['dashboard-data']],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInstallmentSettledSingle() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentSettleSingleRequest) =>
      settledSingleInstallmentPayable(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.notifications.settledSingle.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.notifications.settledSingle.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [
            ['installment-list'],
            ['installment-detail', data.id, data.installmentPayableId],
            ['installmentList-detail', data.id],
          ],
          resetQueries: [
            ['dashboard-data'],
            ['installmentList-detail', data.id],
          ],
        });
      },
      async onError(error: AxiosError, data) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountPayableErrors[err] || err),
        });

        if (handleInstallmentAlreadyEdited(error)) {
          await queryClient.invalidateQueries([
            'installment-detail',
            data.id,
            data.installmentPayableId,
          ]);

          await queryClient.invalidateQueries([
            'installmentList-detail',
            data.id,
          ]);
        }
      },
    },
  );

  return mutation;
}

export function useInstallmentSettledPartially() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentSettlePartiallyRequest) =>
      settledPartiallyInstallmentPayable(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.notifications.settledPartially.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.notifications.settledPartially.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [
            ['installment-list'],
            ['installment-detail', data.id, data.installmentPayableId],
            ['installmentList-detail', data.id],
          ],
          resetQueries: [
            ['dashboard-data'],
            ['installmentList-detail', data.id],
          ],
        });
      },

      async onError(error: AxiosError, data) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountPayableErrors[err] || err),
        });

        if (handleInstallmentAlreadyEdited(error)) {
          await queryClient.invalidateQueries([
            'installment-detail',
            data.id,
            data.installmentPayableId,
          ]);

          await queryClient.invalidateQueries([
            'installmentList-detail',
            data.id,
          ]);
        }
      },
    },
  );

  return mutation;
}

export function useInstallmentSettledMultiple() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation(settledMultipleInstallmentPayable, {
    onSuccess(_, { accountPayableIds }) {
      notification.success({
        message: intl.formatMessage({
          id: 'financial.billsToPay.notifications.settledMultiple.success.title',
        }),
        description: intl.formatMessage({
          id: 'financial.billsToPay.notifications.settledMultiple.success.description',
        }),
      });

      queryChanel({
        invalidateQueries: [
          ...accountPayableIds.map((accountPayableId) => [
            'installmentList-detail',
            accountPayableId,
          ]),
        ],
        resetQueries: [['dashboard-data'], ['installment-list']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useInstallmentReverse() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentReverseRequest) => reverseInstallmentPayable(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: 'Sucesso',
          description: 'Estorno da parcela feito com sucesso',
        });

        queryChanel({
          invalidateQueries: [
            ['installment-detail', data.id, data.installmentPayableId],
            ['installmentList-detail', data.id],
            ['suppliers', data.supplierId],
            ['suppliers-credit'],
          ],
          resetQueries: [['dashboard-data']],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInstallmentSplit() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentSplitRequest) => splitInstallmentPayable(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'financial.billsToPay.notifications.splitInstallment.success.title',
          }),
          description: intl.formatMessage({
            id: 'financial.billsToPay.notifications.splitInstallment.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [
            ['installment-list'],
            [FINANCIAL_CONSULT_PAYABLE_LIST],
            [
              'installment-detail',
              data.accountPayableId,
              data.installmentPayableId,
            ],
            ['installmentList-detail', data.accountPayableId],
          ],
          resetQueries: [['dashboard-data']],
        });
      },

      async onError(error: AxiosError, data) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountPayableErrors[err] || err),
        });

        if (handleInstallmentAlreadyEdited(error)) {
          await queryClient.invalidateQueries([
            'installment-detail',
            data.accountPayableId,
            data.installmentPayableId,
          ]);

          await queryClient.invalidateQueries([
            'installmentList-detail',
            data.accountPayableId,
          ]);
        }
      },
    },
  );

  return mutation;
}

export function useInstallmentRenegotiate() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: InstallmentRenegotiateRequest) => renegotiateAccountPayable(obj),
    {
      onSuccess(response) {
        notification.success({
          message: 'Sucesso',
          description: 'Parcela renegociada com sucesso!',
        });

        queryChanel({
          invalidateQueries: [
            ['installment-list'],
            [FINANCIAL_CONSULT_PAYABLE_LIST],
            ['installment-detail', response.accountId, response.installmentId],
            ['installmentList-detail', response.accountId],
          ],
          resetQueries: [['dashboard-data']],
        });
      },

      async onError(error: AxiosError, data) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountPayableErrors[err] || err),
        });

        if (handleInstallmentAlreadyEdited(error)) {
          await queryClient.invalidateQueries([
            'installment-detail',
            data.accountId,
            data.installmentPayableId,
          ]);

          await queryClient.invalidateQueries([
            'installmentList-detail',
            data.accountId,
          ]);
        }
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useAccountPayableListGroup(
  params?: AccountPayableGroupingRequest,
) {
  const query = useInfiniteQuery(
    ['account-payable-group', { ...params }],
    ({ pageParam }) => {
      const req: AccountPayableGroupingRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return accountPayableListGrouping(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useAccountPayableGenerateGrouping() {
  const queryChanel = useQueryChanel();

  return useMutation(generateGrouping, {
    onSuccess() {
      notification.success({
        message: 'Agrupamento de parcelas efetuado com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          ['installment-list'],
          ['account-payable-group'],
          [FINANCIAL_CONSULT_PAYABLE_LIST],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountPayableRangeDate(
  params?: AccountPayableRangeDateRequest,
) {
  return useQuery(
    ['range-date', params],
    () => accountPayableRangeDate(params),
    {
      onError(error) {
        notificationError(error);
      },
      enabled: !!params?.initialDate && !!params.finalDate,
    },
  );
}

export function useGenerateFinancialByPurchase() {
  const queryChanel = useQueryChanel();
  const mutation = useMutation(
    (obj: GenerateFinancialByPurchase) => generateFinancialByPurchase(obj),
    {
      onSuccess(_, variables) {
        notification.success({
          message: 'Sucesso',
          description: 'Financeiro gerado com sucesso!',
        });

        queryChanel({
          invalidateQueries: [
            ['account-payable-purchase'],
            ['purchase/detail', variables.purchaseId],
          ],
          resetQueries: [['dashboard-data']],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useListAccountPayableByPurchaseId(
  params: AccountPayableListByPurchase,
) {
  const query = useInfiniteQuery(
    ['account-payable-purchase', { ...params }],
    ({ pageParam }) => {
      const req: AccountPayableListByPurchase = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return accountPayableListByPurchase(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useAccountPayableReverseMultipleInstallment() {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateAccountPayableReverseMultipleInstallment, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Parcelas estornadas com sucesso!',
      });

      await queryClient.invalidateQueries(['installment-list']);
      await queryClient.invalidateQueries([FINANCIAL_CONSULT_PAYABLE_LIST]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useAccountPayableDeleteMultipleInstallment() {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateAccountPayableDeleteMultipleInstallment, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Parcelas excluídas com sucesso!',
      });

      await queryClient.invalidateQueries(['installment-list']);
      await queryClient.invalidateQueries([FINANCIAL_CONSULT_PAYABLE_LIST]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useAccountPayableListGrouping(accountPayableId?: number) {
  return useQuery(
    'AccountPayable-ListGrouping',
    () => accountPayableListGroupingById(accountPayableId),
    { enabled: !!accountPayableId },
  );
}

export function useAccountPayableArchiveCreate() {
  return useMutation(accountPayableArchiveCreate, {
    async onSuccess(_, vars) {
      notification.success({ message: 'Anexo vinculado com sucesso!' });

      await queryClient.invalidateQueries([
        'accountPayble/archives',
        { accountPayableId: vars.accountPayableId },
      ]);
      queryClient.removeQueries([
        'accountPayble/archives',
        { accountPayableId: vars.accountPayableId },
      ]);
    },

    onError(err) {
      notificationError(err);
    },
  });
}

export function useAccountPayableArchiveList(accountPayableId?: number) {
  const cache = queryClient.getQueryData([
    'accountPayble/archives',
    { accountPayableId },
  ]);

  return useQuery(
    ['accountPayble/archives', { accountPayableId }],
    () => accountPayableArchiveList(accountPayableId),
    {
      enabled: !!accountPayableId && !cache,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useAccountPayableArchiveRemove() {
  return useMutation(accountPayableArchiveRemove, {
    async onSuccess(_, vars) {
      notification.success({ message: 'Anexo removido com sucesso!' });

      await queryClient.invalidateQueries([
        'accountPayble/archives',
        { accountPayableId: vars.accountPayableId },
      ]);
      queryClient.removeQueries([
        'accountPayble/archives',
        { accountPayableId: vars.accountPayableId },
      ]);
    },

    onError(err) {
      notificationError(err);
    },
  });
}

export function useAccountPayableIncludeTag() {
  const queryChanel = useQueryChanel();

  return useMutation(accountPayableIncludeTag, {
    onSuccess(_, data) {
      notification.success({
        message: `Etiqueta ${
          !!data.installmentTagId ? 'vinculada' : 'desvinculada'
        } à conta a pagar`,
      });

      queryChanel({
        invalidateQueries: [['installmentList-detail', data.accountPayableId]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
